/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { runScheduleGeneration } from './scheduleGenerator';
import DayTimeline from './DayTimeline';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MEAL_PERIODS = ['Breakfast', 'Lunch', 'Dinner', 'Late Night'];

const ROLE_COLORS = {
  'Cook': 'border-orange-200',
  'Prep Cook': 'border-red-200',
  'Dishwasher': 'border-slate-200',
  'Server': 'border-blue-200',
  'Bartender': 'border-amber-200',
  'Host': 'border-purple-200',
  'Busser': 'border-teal-200',
  'Manager': 'border-gray-200',
};

// Premium muted shift card styles — role-tinted backgrounds
const ROLE_CARD_STYLE = {
  'Cook':      { background: '#FED7AA', color: '#7C2D12', borderColor: '#F97316' },
  'Prep Cook': { background: '#FECACA', color: '#7F1D1D', borderColor: '#EF4444' },
  'Dishwasher':{ background: '#E2E8F0', color: '#334155', borderColor: '#64748B' },
  'Server':    { background: '#BFDBFE', color: '#1E3A8A', borderColor: '#3B82F6' },
  'Bartender': { background: '#FDE68A', color: '#78350F', borderColor: '#F59E0B' },
  'Host':      { background: '#E9D5FF', color: '#581C87', borderColor: '#A855F7' },
  'Busser':    { background: '#99F6E4', color: '#134E4A', borderColor: '#14B8A6' },
  'Manager':   { background: '#E5E7EB', color: '#1F2937', borderColor: '#6B7280' },
};

const ROLE_TYPE = {
  'Cook': 'BOH', 'Prep Cook': 'BOH', 'Dishwasher': 'BOH',
  'Server': 'FOH', 'Bartender': 'FOH', 'Host': 'FOH', 'Busser': 'FOH', 'Manager': 'FOH',
};

// Data colors — per DESIGN_SYSTEM.md Brand/UI vs Data Colors, role palette stays hardcoded.
const ROLE_SOLID = {
  'Cook': '#EA580C', 'Prep Cook': '#DC2626', 'Dishwasher': '#64748B',
  'Server': '#2563EB', 'Bartender': '#D97706', 'Host': '#9333EA',
  'Busser': '#0D9488', 'Manager': '#4B5563',
};

const ROLE_BAR_TEXT = {
  'Cook': '#fff', 'Prep Cook': '#fff', 'Dishwasher': '#fff',
  'Server': '#fff', 'Bartender': '#fff', 'Host': '#fff',
  'Busser': '#fff', 'Manager': '#fff',
};

const ROLE_ORDER = ['Cook', 'Prep Cook', 'Dishwasher', 'Server', 'Bartender', 'Host', 'Busser', 'Manager'];

const PERIOD_DEFAULTS = {
  'Breakfast': { start: '06:00', end: '11:00' },
  'Lunch': { start: '11:00', end: '16:00' },
  'Dinner': { start: '16:00', end: '23:00' },
  'Late Night': { start: '22:00', end: '02:00' },
};

function getRoleColor(role) {
  return ROLE_COLORS[role] || 'bg-green-100 text-green-700 border-green-200';
}

function getWeekDates(weekOffset = 0) {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(now);
  monday.setDate(diff + weekOffset * 7);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function formatDate(d) { return d.toISOString().split('T')[0]; }

function calcHours(start, end) {
  if (!start || !end) return 7;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let h = (eh * 60 + em - sh * 60 - sm) / 60;
  if (h < 0) h += 24;
  return Math.round(h * 10) / 10;
}

function formatTime(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

function shortTime(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const hour = h % 12 || 12;
  const p = h >= 12 ? 'p' : 'a';
  return m === 0 ? `${hour}${p}` : `${hour}:${String(m).padStart(2, '0')}${p}`;
}

function applyStagger(period, role, stagger, offsetIndex) {
  const defaultStart = PERIOD_DEFAULTS[period]?.start || '10:00';
  // If stagger is disabled globally, use default
  if (!stagger || !stagger.role_offsets || stagger.enabled === false) return defaultStart;
  // If this role has stagger disabled, use default
  if (Array.isArray(stagger.disabled_roles) && stagger.disabled_roles.includes(role)) return defaultStart;
  // First person (offsetIndex 0) always starts at the period default to ensure coverage
  if (offsetIndex === 0) return defaultStart;
  const offset = stagger.role_offsets[role];
  if (offset === undefined || offset === null) return defaultStart;
  const rushTime = period === 'Lunch' ? stagger.lunch_rush_time
    : period === 'Breakfast' ? (stagger.brunch_rush_time || '09:00')
    : stagger.dinner_rush_time;
  if (!rushTime) return defaultStart;
  const [rh, rm] = rushTime.split(':').map(Number);
  const total = rh * 60 + rm + offset + (offsetIndex * 15);
  const newH = Math.floor(((total % 1440) + 1440) % 1440 / 60);
  const newM = ((total % 60) + 60) % 60;
  const [psh, psm] = defaultStart.split(':').map(Number);
  const [peh, pem] = (PERIOD_DEFAULTS[period]?.end || '23:00').split(':').map(Number);
  const staggeredMins = newH * 60 + newM;
  const periodStartMins = psh * 60 + psm;
  const periodEndMins = peh * 60 + pem;
  if (staggeredMins >= periodEndMins || staggeredMins < periodStartMins - 60) {
    return defaultStart;
  }
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}

export default function Dashboard({ companyId, onEmployeeView, onNavigate }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [template, setTemplate] = useState({});
  const [staffingLevels, setStaffingLevels] = useState({});
  const [stagger, setStagger] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalCell, setModalCell] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('Dinner');
  const [selectedRole, setSelectedRole] = useState('');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [generating, setGenerating] = useState(false);
  const [published, setPublished] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('All');
  const [viewMode, setViewMode] = useState('week');
  const [ganttDayIndex, setGanttDayIndex] = useState(() => { const d = new Date().getDay(); return d === 0 ? 6 : d - 1; });
  const [showConflicts, setShowConflicts] = useState(false);
  const [conflicts, setConflicts] = useState({ critical: [], warnings: [], info: [] });
  const [editingShift, setEditingShift] = useState(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [collapsedRoles, setCollapsedRoles] = useState({});
  const [collapsedPeriods, setCollapsedPeriods] = useState({});
  const [ganttTooltip, setGanttTooltip] = useState(null);
  const [score, setScore] = useState(null);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [companyRoles, setCompanyRoles] = useState(null);
  const [needMoreTooltip, setNeedMoreTooltip] = useState(null); // { key, x, y, items }
  const [profileEmp, setProfileEmp] = useState(null); // employee object opened in the By Role profile modal
  const [dragError, setDragError] = useState(null); // transient error shown after an invalid drop in By Role view
  const [overtimeThreshold, setOvertimeThreshold] = useState(40);
  const [endCodes, setEndCodes] = useState([]); // [{ code, label }]
  const [editEndCode, setEditEndCode] = useState(''); // '' means "no code, use end_time"
  const [extraShifts, setExtraShifts] = useState([]); // expanded for the current week
  const [extraModal, setExtraModal] = useState(null); // null | { mode, ...form }
  const [showHelpPopover, setShowHelpPopover] = useState(false);
  const [newShiftRecurring, setNewShiftRecurring] = useState(false);
  const [publishJustFired, setPublishJustFired] = useState(false); // triggers the badge pop + toast after publish
  const [timeOffData, setTimeOffData] = useState([]); // [{ name, role, color, dates: [dateStr] }]
  const [timeOffByEmpId, setTimeOffByEmpId] = useState({}); // { empId: Set(dateStr) }
  const [recurringPreviews, setRecurringPreviews] = useState([]); // virtual shift objects

  const weekDates = getWeekDates(weekOffset);
  const weekLabel = `${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  useEffect(() => {
    if (!companyId) return;
    setNeedMoreTooltip(null);
    fetchData();
  }, [companyId, weekOffset]);

  async function fetchData() {
    const dateStrings = weekDates.map(formatDate);
    const weekStarting = formatDate(weekDates[0]);
    const [empsRes, shiftsRes, tplRes, staffRes, staggerRes, rolesRes, timeOffRes, companyRes, extrasRes, shiftTemplatesRes, publishRes] = await Promise.all([
      supabase.from('employees').select('*').eq('company_id', companyId),
      supabase.from('shifts').select('*').eq('company_id', companyId).in('day', dateStrings),
      supabase.from('service_templates').select('*').eq('company_id', companyId),
      supabase.from('staffing_levels').select('*').eq('company_id', companyId),
      supabase.from('smart_stagger').select('*').eq('company_id', companyId).single(),
      supabase.from('roles').select('name').eq('company_id', companyId),
      supabase.from('requests').select('*').eq('company_id', companyId).eq('type', 'time_off').eq('status', 'approved'),
      supabase.from('companies').select('overtime_weekly_hours, schedule_end_codes').eq('id', companyId).single(),
      supabase.from('extra_shifts').select('*').eq('company_id', companyId).eq('is_active', true),
      supabase.from('shifts').select('*').eq('company_id', companyId).eq('is_recurring', true).not('day_of_week', 'is', null),
      supabase.from('schedule_weeks').select('published_at').eq('company_id', companyId).eq('week_starting', weekStarting).maybeSingle(),
    ]);
    setPublished(!!publishRes?.data?.published_at);
    const emps = empsRes.data || [];
    // Exclude templates from the week's concrete shifts (their `day` is the creation date, which might
    // fall in the current week and cause a double-render).
    const rawWeekShifts = (shiftsRes.data || []).filter(s => !(s.is_recurring && s.day_of_week));
    // Expand recurring templates into virtual shifts on the matching weekday of THIS week.
    // Skip if a real shift already exists for the same (employee, date, period, role).
    const dateByDow = {};
    weekDates.forEach((d, i) => { dateByDow[DAYS[i]] = formatDate(d); });
    const templateExpansions = [];
    (shiftTemplatesRes.data || []).forEach(tpl => {
      const dateStr = dateByDow[tpl.day_of_week];
      if (!dateStr) return;
      const collision = rawWeekShifts.some(s => !s.is_recurring && String(s.employee_id) === String(tpl.employee_id) && s.day === dateStr && s.shift_name === tpl.shift_name && s.role === tpl.role);
      if (collision) return;
      templateExpansions.push({ ...tpl, _previewId: `tpl-${tpl.id}-${dateStr}`, _isRecurring: true, day: dateStr });
    });
    const realShifts = [...rawWeekShifts, ...templateExpansions];
    // Expand extras into virtual shift-like objects for this week.
    // Recurring ones fire on each matching weekday; one-offs only appear on their specific_date.
    const dateSet = new Set(dateStrings);
    const extras = [];
    (extrasRes.data || []).forEach(row => {
      if (row.is_recurring) {
        const dateStr = dateByDow[row.day_of_week];
        if (!dateStr) return;
        extras.push({ ...row, _isExtra: true, day: dateStr });
      } else if (row.specific_date && dateSet.has(row.specific_date)) {
        extras.push({ ...row, _isExtra: true, day: row.specific_date });
      }
    });
    setExtraShifts(extras);
    setEmployees(emps);
    setShifts(realShifts);
    if (companyRes.data) {
      setOvertimeThreshold(Number(companyRes.data.overtime_weekly_hours) || 40);
      setEndCodes(Array.isArray(companyRes.data.schedule_end_codes) ? companyRes.data.schedule_end_codes : []);
    }
    if (rolesRes.data && rolesRes.data.length > 0) {
      setCompanyRoles(rolesRes.data.map(r => r.name));
    } else {
      setCompanyRoles(null);
    }

    let tpl = {};
    if (tplRes.data && tplRes.data.length > 0) {
      tplRes.data.filter(t => t.is_active).forEach(t => {
        if (!tpl[t.day_of_week]) tpl[t.day_of_week] = [];
        tpl[t.day_of_week].push(t.meal_period);
      });
    } else {
      tpl = { Mon: ['Lunch', 'Dinner'], Tue: ['Lunch', 'Dinner'], Wed: ['Lunch', 'Dinner'], Thu: ['Lunch', 'Dinner'], Fri: ['Lunch', 'Dinner'], Sat: ['Breakfast', 'Lunch', 'Dinner'], Sun: ['Breakfast', 'Lunch'] };
    }
    setTemplate(tpl);

    if (staffRes.data) {
      const s = {};
      staffRes.data.forEach(d => { s[`${d.day_of_week}-${d.meal_period}-${d.role}`] = d.required_count; });
      setStaffingLevels(s);
    }
    if (staggerRes.data) setStagger(staggerRes.data);

    // ── Parse time off ──
    const weekDateSet = new Set(dateStrings);
    const timeOffByName = {};
    (timeOffRes.data || []).forEach(r => {
      const dates = (r.detail || '').match(/\d{4}-\d{2}-\d{2}/g) || [];
      const weekDatesOff = dates.filter(d => weekDateSet.has(d));
      if (weekDatesOff.length === 0) return;
      const emp = emps.find(e => e.name === r.employee_name);
      const name = r.employee_name || 'Unknown';
      if (!timeOffByName[name]) {
        timeOffByName[name] = {
          name,
          role: emp ? (typeof emp.role === 'string' ? emp.role.split(',')[0].trim() : '') : '',
          color: r.color || (emp ? emp.color : ''),
          initials: r.initials || (emp ? emp.initials : ''),
          dates: [],
        };
      }
      weekDatesOff.forEach(d => { if (!timeOffByName[name].dates.includes(d)) timeOffByName[name].dates.push(d); });
    });
    setTimeOffData(Object.values(timeOffByName));

    // ── Build blocked time off set for recurring + modal lookups ──
    const blockedTimeOff = new Set();
    const empIdTimeOff = {};
    (timeOffRes.data || []).forEach(r => {
      const emp = emps.find(e => e.name === r.employee_name);
      if (!emp) return;
      const dates = (r.detail || '').match(/\d{4}-\d{2}-\d{2}/g) || [];
      dates.forEach(d => {
        blockedTimeOff.add(`${emp.id}-${d}`);
        if (!empIdTimeOff[emp.id]) empIdTimeOff[emp.id] = new Set();
        empIdTimeOff[emp.id].add(d);
      });
    });
    // Convert Sets to arrays for state (Sets aren't serializable but we just need .has())
    const empIdTimeOffObj = {};
    Object.entries(empIdTimeOff).forEach(([id, dates]) => { empIdTimeOffObj[id] = [...dates]; });
    setTimeOffByEmpId(empIdTimeOffObj);

    // ── Compute recurring previews ──
    const previews = [];
    emps.forEach(emp => {
      const recurring = Array.isArray(emp.recurring) ? emp.recurring : [];
      recurring.forEach(rec => {
        if (!rec.active) return;
        const di = DAYS.indexOf(rec.day);
        if (di === -1) return;
        if (!(tpl[rec.day] || []).includes(rec.shift)) return;
        const dateStr = formatDate(weekDates[di]);
        // Skip if time off
        if (blockedTimeOff.has(`${emp.id}-${dateStr}`)) return;
        // Skip if unavailable (JSONB)
        const unavail = Array.isArray(emp.unavailable) ? emp.unavailable : [];
        const dayBlock = unavail.find(u => u.day === rec.day);
        if (dayBlock && (dayBlock.allDay || (dayBlock.periods && dayBlock.periods.includes(rec.shift)))) return;
        // Skip if a real shift already exists for this employee/date/period
        if (realShifts.some(s => String(s.employee_id) === String(emp.id) && s.day === dateStr && s.shift_name === rec.shift)) return;
        const pd = PERIOD_DEFAULTS[rec.shift] || { start: '10:00', end: '23:00' };
        previews.push({
          _isRecurring: true,
          _previewId: `rec-${emp.id}-${dateStr}-${rec.shift}-${rec.role}`,
          employee_id: emp.id,
          day: dateStr,
          shift_name: rec.shift,
          role: rec.role,
          start_time: pd.start,
          end_time: pd.end,
        });
      });
    });
    setRecurringPreviews(previews);

    // ── Auto-collapse empty periods ──
    const allWeekShifts = [...realShifts, ...previews];
    const autoCollapsed = {};
    MEAL_PERIODS.forEach(period => {
      const hasShifts = allWeekShifts.some(s => s.shift_name === period);
      const hasStaffing = staffRes.data && staffRes.data.some(sl => sl.meal_period === period && sl.required_count > 0);
      const hasTemplate = Object.values(tpl).some(periods => periods.includes(period));
      if (!hasShifts && (!hasStaffing || !hasTemplate)) {
        autoCollapsed[period] = true;
      }
    });
    setCollapsedPeriods(prev => {
      // Only auto-collapse if user hasn't manually toggled
      const merged = { ...autoCollapsed };
      Object.keys(prev).forEach(k => { if (prev[k] === false) delete merged[k]; });
      return merged;
    });
  }

  // ─── SCHEDULE GENERATION ─────────────────────────────────────────────────
  async function generateSchedule() {
    if (!companyId) return;
    setGenerating(true);
    try {
      const dateStrings = weekDates.map(formatDate);

      // Warn if shifts already exist
      const existingCheck = await supabase.from('shifts').select('id').eq('company_id', companyId).in('day', dateStrings).limit(1);
      if (existingCheck.data && existingCheck.data.length > 0) {
        const confirm = window.confirm('This will replace your current schedule. Continue?');
        if (!confirm) { setGenerating(false); return; }
      }

      const result = await runScheduleGeneration(companyId, weekOffset);
      await fetchData();
      setScore(result);
      setShowScoreModal(true);
    } catch (e) {
      console.error('Schedule generation failed:', e);
      alert('Schedule generation failed — check console.');
    }
    setGenerating(false);
  }

  // ─── CONFLICT DETECTION ───────────────────────────────────────────────────
  function detectConflicts(shiftData, empData) {
    const critical = [], warnings = [], info = [];
    const s = shiftData || shifts;
    const e = empData || employees;
    e.forEach(emp => {
      const empShifts = s.filter(sh => String(sh.employee_id) === String(emp.id));
      const avail = (emp.availability || '').split(',').map(d => d.trim()).filter(Boolean);
      const byDay = {};
      empShifts.forEach(sh => { if (!byDay[sh.day]) byDay[sh.day] = []; byDay[sh.day].push(sh); });
      Object.entries(byDay).forEach(([day, dayShifts]) => {
        if (dayShifts.length > 1) {
          for (let i = 0; i < dayShifts.length; i++) {
            for (let j = i + 1; j < dayShifts.length; j++) {
              if (dayShifts[i].shift_name === dayShifts[j].shift_name) {
                critical.push({ message: `${emp.name} is double-booked on ${day} (${dayShifts[i].shift_name})` });
              } else {
                info.push({ message: `${emp.name} working a double on ${day} (${dayShifts[i].shift_name} + ${dayShifts[j].shift_name})` });
              }
            }
          }
        }
      });
      empShifts.forEach(sh => {
        const d = new Date(sh.day + 'T12:00:00');
        const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
        if (avail.length > 0 && !avail.some(a => dayName.startsWith(a))) {
          critical.push({ message: `${emp.name} scheduled on ${sh.day} but marked unavailable` });
        }
      });
      const totalHrs = empShifts.reduce((acc, sh) => acc + calcHours(sh.start_time, sh.end_time), 0);
      const overtimeThreshold = emp.max_hours_per_week || 40;
      if (totalHrs > overtimeThreshold) warnings.push({ message: `${emp.name} is at ${Math.round(totalHrs)} hrs (overtime)` });
      if (emp.max_hours_per_week && totalHrs > emp.max_hours_per_week) {
        warnings.push({ message: `${emp.name} exceeds max ${emp.max_hours_per_week} hrs (scheduled ${Math.round(totalHrs)})` });
      }
      const sorted = [...empShifts].sort((a, b) => a.day.localeCompare(b.day));
      for (let i = 0; i < sorted.length - 1; i++) {
        const curr = sorted[i], next = sorted[i + 1];
        const diff = (new Date(next.day + 'T12:00:00') - new Date(curr.day + 'T12:00:00')) / 86400000;
        if (diff === 1) {
          const endH = curr.end_time ? parseInt(curr.end_time) : 0;
          const startH = next.start_time ? parseInt(next.start_time) : 12;
          if (endH >= 22 && startH <= 8) warnings.push({ message: `${emp.name} has a clopen: closes ${curr.day}, opens ${next.day}` });
        }
      }
      const target = emp.target_hours || 30;
      if (totalHrs > 0 && totalHrs < target * 0.7) info.push({ message: `${emp.name} at ${Math.round(totalHrs)} hrs vs ${target} hr target` });
    });
    return { critical, warnings, info };
  }

  // ─── SCHEDULE SCORE ───────────────────────────────────────────────────────
  function calculateScore(shiftData, empData, tplData, staffData) {
    const s = shiftData || shifts;
    const e = empData || employees;
    const tpl = tplData || template;
    const staffLvls = staffData || staffingLevels;
    let sc = 100;
    const deductions = [];
    let hasClopen = false;
    e.forEach(emp => {
      const empShifts = s.filter(sh => String(sh.employee_id) === String(emp.id)).sort((a, b) => a.day.localeCompare(b.day));
      for (let i = 0; i < empShifts.length - 1; i++) {
        const diff = (new Date(empShifts[i + 1].day + 'T12:00:00') - new Date(empShifts[i].day + 'T12:00:00')) / 86400000;
        if (diff === 1 && parseInt(empShifts[i].end_time) >= 22 && parseInt(empShifts[i + 1].start_time) <= 8) hasClopen = true;
      }
    });
    if (hasClopen) { sc -= 20; deductions.push({ text: 'Clopen detected', pts: -20 }); }
    let hasStreak = false;
    e.forEach(emp => {
      const days = [...new Set(s.filter(sh => String(sh.employee_id) === String(emp.id)).map(sh => sh.day))].sort();
      let streak = 1;
      for (let i = 1; i < days.length; i++) {
        const diff = (new Date(days[i] + 'T12:00:00') - new Date(days[i - 1] + 'T12:00:00')) / 86400000;
        if (diff === 1) { streak++; if (streak >= 6) hasStreak = true; } else streak = 1;
      }
    });
    if (hasStreak) { sc -= 15; deductions.push({ text: '6+ day streak detected', pts: -15 }); }
    let hasOvertime = false;
    e.forEach(emp => {
      const maxH = emp.max_hours_per_week || 40;
      if (s.filter(sh => String(sh.employee_id) === String(emp.id)).reduce((a, sh) => a + calcHours(sh.start_time, sh.end_time), 0) > maxH) hasOvertime = true;
    });
    if (hasOvertime) { sc -= 10; deductions.push({ text: 'Overtime detected', pts: -10 }); }
    let hasAvailConflict = false;
    e.forEach(emp => {
      const avail = (emp.availability || '').split(',').map(d => d.trim()).filter(Boolean);
      if (!avail.length) return;
      s.filter(sh => String(sh.employee_id) === String(emp.id)).forEach(sh => {
        const dn = new Date(sh.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' });
        if (!avail.some(a => dn.startsWith(a))) hasAvailConflict = true;
      });
    });
    if (hasAvailConflict) { sc -= 10; deductions.push({ text: 'Availability violation', pts: -10 }); }
    let uncovered = 0, totalReq = 0;
    DAYS.forEach((day, di) => {
      (tpl[day] || []).forEach(period => {
        Object.entries(staffLvls).forEach(([key, req]) => {
          if (key.startsWith(`${day}-${period}-`) && req > 0) {
            totalReq += req;
            const role = key.split('-').slice(2).join('-');
            const filled = s.filter(sh => sh.day === formatDate(weekDates[di]) && sh.shift_name === period && sh.role === role).length;
            if (filled < req) uncovered += (req - filled);
          }
        });
      });
    });
    if (totalReq > 0 && uncovered > 0) {
      const ded = Math.min(20, Math.round((uncovered / totalReq) * 20));
      if (ded > 0) { sc -= ded; deductions.push({ text: `${uncovered} understaffed slot(s)`, pts: -ded }); }
    }
    return { score: Math.max(0, sc), deductions };
  }

  // ─── PUBLISH ──────────────────────────────────────────────────────────────
  // ── SMS helper ──
  async function sendSMS(to, body) {
    if (!to) return;
    try {
      await fetch('/api/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, body }),
      });
    } catch (e) { console.error('SMS send failed:', e); }
  }

  async function notifyPublish() {
    // Notify all employees who have shifts this week
    const empsWithShifts = [...new Set(shifts.map(s => String(s.employee_id)))];
    for (const empId of empsWithShifts) {
      const emp = employees.find(e => String(e.id) === empId);
      if (!emp?.phone) continue;
      sendSMS(emp.phone, `Posted: Your schedule for ${weekLabel} is published. Check the app for your shifts.`);
    }
  }

  async function persistPublish() {
    const weekStarting = formatDate(weekDates[0]);
    const { data: { user } } = await supabase.auth.getUser();
    const nowIso = new Date().toISOString();
    await supabase.from('schedule_weeks').upsert(
      {
        company_id: companyId,
        week_starting: weekStarting,
        published_at: nowIso,
        published_by: user?.email || null,
        updated_at: nowIso,
      },
      { onConflict: 'company_id,week_starting' }
    );
  }

  async function publishSchedule() {
    const detected = detectConflicts(shifts, employees);
    setConflicts(detected);
    if (detected.critical.length > 0 || detected.warnings.length > 0) { setShowConflicts(true); return; }
    await persistPublish();
    setPublished(true);
    setPublishJustFired(true);
    notifyPublish();
    // publishJustFired drives the toast + badge-entrance animation only; the badge itself
    // stays because `published` is bound to the persisted schedule_weeks row now.
    setTimeout(() => setPublishJustFired(false), 2500);
  }

  async function forcePublish() {
    setShowConflicts(false);
    await persistPublish();
    setPublished(true);
    setPublishJustFired(true);
    notifyPublish();
    setTimeout(() => setPublishJustFired(false), 2500);
  }

  async function unpublishSchedule() {
    if (unpublishing) return;
    setUnpublishing(true);
    const weekStarting = formatDate(weekDates[0]);
    const nowIso = new Date().toISOString();
    // Keep the row for history — null the timestamp only.
    await supabase.from('schedule_weeks')
      .update({ published_at: null, updated_at: nowIso })
      .eq('company_id', companyId)
      .eq('week_starting', weekStarting);
    setPublished(false);
    setUnpublishing(false);
  }

  // ─── GRID HELPERS ─────────────────────────────────────────────────────────
  // Roles to render as rows: employees on roster PLUS any role that staffing_levels says
  // we need (even when there's no employee for it yet). Without the union the schedule
  // hid required-but-unstaffed roles like Dishwasher / Manager / Server, leaving the
  // manager with no visual cue that anything was missing.
  const employeeRoles = [...new Set(employees.flatMap(e => typeof e.role === 'string' ? e.role.split(',').map(r => r.trim()) : (e.role || [])))].filter(Boolean);
  const requiredRoles = [...new Set(
    Object.entries(staffingLevels)
      .filter(([, count]) => Number(count) > 0)
      .map(([key]) => {
        const parts = key.split('-');
        return parts.length >= 3 ? parts.slice(2).join('-') : null;
      })
      .filter(Boolean)
  )];
  // Show every role that has an employee or staffing requirement, plus anything explicitly
  // defined in the company's roles taxonomy. We used to filter by companyRoles alone, which
  // silently hid employees assigned to a role that wasn't in the taxonomy (e.g. Prep Cook).
  const allRoles = [...new Set([...(companyRoles || []), ...employeeRoles, ...requiredRoles])];
  const activePeriods = [...new Set(DAYS.flatMap(d => template[d] || []))].filter(p => MEAL_PERIODS.includes(p)).sort((a, b) => MEAL_PERIODS.indexOf(a) - MEAL_PERIODS.indexOf(b));

  function getGridRows() {
    return activePeriods.flatMap(period =>
      allRoles.filter(role => {
        if (filterType === 'FOH') return ROLE_TYPE[role] !== 'BOH';
        if (filterType === 'BOH') return ROLE_TYPE[role] === 'BOH';
        return true;
      }).map(role => ({ period, role }))
    );
  }

  function getShiftsForCell(period, role, date) {
    const dateStr = formatDate(date);
    const real = shifts.filter(s => s.role === role && s.day === dateStr && s.shift_name === period).map(s => {
      const pd = PERIOD_DEFAULTS[s.shift_name];
      if (pd && s.start_time) {
        const [sh, sm] = s.start_time.split(':').map(Number);
        const startMin = sh * 60 + sm;
        const [psh, psm] = pd.start.split(':').map(Number);
        const [peh, pem] = pd.end.split(':').map(Number);
        const periodStart = psh * 60 + psm;
        const periodEnd = peh * 60 + pem;
        if (periodEnd > periodStart) {
          if (startMin >= periodEnd || startMin < periodStart - 60) return { ...s, start_time: pd.start };
        }
      }
      return s;
    });
    // Add recurring previews for this cell
    const previews = recurringPreviews.filter(p => p.role === role && p.day === dateStr && p.shift_name === period);
    return [...real, ...previews];
  }
  function getEmployee(empId) { return employees.find(e => String(e.id) === String(empId)); }
  function getRequiredCount(day, period, role) { return staffingLevels[`${day}-${period}-${role}`] || 0; }

  function openModal(date, period, role) {
    setModalCell({ date, period, role });
    setSelectedRole(role);
    setSelectedPeriod(period);
    setSelectedEmployee('');
    const d = PERIOD_DEFAULTS[period] || { start: '10:00', end: '18:00' };
    setCustomStart(d.start);
    setCustomEnd(d.end);
    setShowModal(true);
  }

  async function saveShift() {
    if (!selectedEmployee) return;
    const match = eligibleEmployees(selectedRole).find(e => String(e.id) === String(selectedEmployee));
    if (match && match.reasons.length > 0) {
      if (!window.confirm(`Override scheduling rules?\n\n${match.name}: ${match.reasons.join(', ')}\n\nSchedule anyway?`)) return;
    }
    setSaving(true);
    // Recurring templates store day_of_week + is_recurring=true; the real `day` is set to the
    // original creation date but is ignored by display (fetchData expands templates into each
    // week's matching weekday).
    const dowIndex = modalCell.date.getDay() === 0 ? 6 : modalCell.date.getDay() - 1;
    const row = {
      company_id: companyId,
      employee_id: parseInt(selectedEmployee),
      day: formatDate(modalCell.date),
      shift_name: selectedPeriod,
      start_time: customStart,
      end_time: customEnd,
      role: selectedRole,
      is_recurring: newShiftRecurring,
      day_of_week: newShiftRecurring ? DAYS[dowIndex] : null,
      is_active: true,
    };
    await supabase.from('shifts').insert([row]);
    setSaving(false); setShowModal(false); setNewShiftRecurring(false); fetchData();
  }

  async function deleteShift(shiftId) {
    const shift = shifts.find(s => s.id === shiftId);
    await supabase.from('shifts').delete().eq('id', shiftId);
    if (shift) {
      const emp = employees.find(e => String(e.id) === String(shift.employee_id));
      if (emp?.phone) {
        const dayLabel = new Date(shift.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        sendSMS(emp.phone, `Posted: Your ${shift.shift_name} shift on ${dayLabel} has been removed.`);
      }
    }
    fetchData();
  }

  async function saveInlineEdit() {
    if (!editingShift) return;
    const shift = shifts.find(s => s.id === editingShift);
    const endCode = editEndCode || null;
    await supabase.from('shifts').update({ start_time: editStart, end_time: editEnd, end_code: endCode }).eq('id', editingShift);
    if (shift) {
      const emp = employees.find(e => String(e.id) === String(shift.employee_id));
      if (emp?.phone) {
        const dayLabel = new Date(shift.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        const endLabel = endCode || shortTime(editEnd);
        sendSMS(emp.phone, `Posted: Shift updated — ${shift.shift_name} on ${dayLabel} is now ${shortTime(editStart)}–${endLabel}.`);
      }
    }
    setEditingShift(null);
    setEditEndCode('');
    fetchData();
  }

  function toMinutes(t) {
    if (!t) return 0;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + (m || 0);
  }
  function shiftsOverlap(a, b) {
    // Treat end-before-start as an overnight shift by wrapping +24h.
    const aStart = toMinutes(a.start_time);
    let aEnd = toMinutes(a.end_time); if (aEnd <= aStart) aEnd += 24 * 60;
    const bStart = toMinutes(b.start_time);
    let bEnd = toMinutes(b.end_time); if (bEnd <= bStart) bEnd += 24 * 60;
    return aStart < bEnd && bStart < aEnd;
  }

  async function reassignShift(shiftId, targetEmp, targetRole) {
    const shift = shifts.find(s => String(s.id) === String(shiftId));
    if (!shift) return;
    if (String(shift.employee_id) === String(targetEmp.id) && shift.role === targetRole) return;

    const empRoles = typeof targetEmp.role === 'string' ? targetEmp.role.split(',').map(r => r.trim()) : (targetEmp.role || []);
    if (!empRoles.includes(targetRole)) {
      setDragError(`${targetEmp.name} isn't qualified as ${targetRole}. Add the role in the employee profile first.`);
      setTimeout(() => setDragError(null), 4000);
      return;
    }

    // Block drops that would double-book the target employee on the same day.
    const conflict = shifts.find(s => String(s.employee_id) === String(targetEmp.id) && s.day === shift.day && String(s.id) !== String(shift.id) && shiftsOverlap(s, shift));
    if (conflict) {
      setDragError(`${targetEmp.name} is already scheduled ${shortTime(conflict.start_time)}–${shortTime(conflict.end_time)} on that day.`);
      setTimeout(() => setDragError(null), 4000);
      return;
    }

    await supabase.from('shifts').update({ employee_id: targetEmp.id, role: targetRole }).eq('id', shiftId);
    fetchData();
  }

  async function saveGanttDrag(shiftId, newStart, newEnd) {
    await supabase.from('shifts').update({ start_time: newStart, end_time: newEnd }).eq('id', shiftId);
    fetchData();
  }

  function hasTimeOff(empId, dateStr) {
    const dates = timeOffByEmpId[empId];
    return dates ? dates.includes(dateStr) : false;
  }

  function eligibleEmployees(role) {
    if (!modalCell) return [];
    const dateStr = formatDate(modalCell.date);
    const dayName = DAYS[modalCell.date.getDay() === 0 ? 6 : modalCell.date.getDay() - 1];
    const dayShifts = shifts.filter(s => s.day === dateStr);
    const periodHrs = calcHours(PERIOD_DEFAULTS[modalCell.period]?.start || '10:00', PERIOD_DEFAULTS[modalCell.period]?.end || '23:00');

    return employees
      .filter(e => {
        const roles = typeof e.role === 'string' ? e.role.split(',').map(r => r.trim()) : (e.role || []);
        return roles.includes(role);
      })
      .map(e => {
        const reasons = [];
        // Unavailability (JSONB) — check first, most specific
        const unavail = Array.isArray(e.unavailable) ? e.unavailable : [];
        const dayBlock = unavail.find(u => u.day === dayName);
        if (dayBlock) {
          if (dayBlock.allDay) reasons.push(`unavailable ${dayName}s`);
          else if (dayBlock.periods && dayBlock.periods.includes(modalCell.period)) reasons.push(`unavailable ${dayName} ${modalCell.period}`);
        }
        // Availability whitelist
        const avail = (e.availability || '').split(',').map(d => d.trim()).filter(Boolean);
        if (avail.length > 0 && !avail.includes(dayName)) reasons.push('not available today');
        // Time off
        if (hasTimeOff(e.id, dateStr)) reasons.push('TIME OFF');
        // Already scheduled
        if (dayShifts.some(s => String(s.employee_id) === String(e.id) && s.shift_name === modalCell.period)) reasons.push('already scheduled');
        // Max hours
        const empShiftHrs = shifts.filter(s => String(s.employee_id) === String(e.id)).reduce((a, s) => a + calcHours(s.start_time, s.end_time), 0);
        const maxH = e.max_hours_per_week || 40;
        if (empShiftHrs + periodHrs > maxH) reasons.push(`max hours (${Math.round(empShiftHrs)}/${maxH})`);
        // Clopen
        if (modalCell.period === 'Breakfast') {
          const prevDate = new Date(modalCell.date); prevDate.setDate(prevDate.getDate() - 1);
          if (shifts.some(s => String(s.employee_id) === String(e.id) && s.day === formatDate(prevDate) && s.shift_name === 'Dinner')) reasons.push('clopen risk');
        }
        return { ...e, reasons, disabled: reasons.length > 0, hasTimeOff: hasTimeOff(e.id, dateStr) };
      })
      .sort((a, b) => a.disabled - b.disabled);
  }

  function getRejectionReasons(period, role, date) {
    const dateStr = formatDate(date);
    const dayName = DAYS[date.getDay() === 0 ? 6 : date.getDay() - 1];
    const dayShifts = shifts.filter(s => s.day === dateStr);
    const periodHrs = calcHours(PERIOD_DEFAULTS[period]?.start || '10:00', PERIOD_DEFAULTS[period]?.end || '23:00');

    return employees
      .filter(e => {
        const roles = typeof e.role === 'string' ? e.role.split(',').map(r => r.trim()) : (e.role || []);
        return roles.includes(role);
      })
      .map(e => {
        const reasons = [];
        if (hasTimeOff(e.id, dateStr)) reasons.push('Time off');
        const avail = (e.availability || '').split(',').map(d => d.trim()).filter(Boolean);
        if (avail.length > 0 && !avail.includes(dayName)) reasons.push('Not available');
        const unavail = Array.isArray(e.unavailable) ? e.unavailable : [];
        const dayBlock = unavail.find(u => u.day === dayName);
        if (dayBlock) {
          if (dayBlock.allDay) reasons.push(`Unavailable ${dayName}s`);
          else if (dayBlock.periods && dayBlock.periods.includes(period)) reasons.push(`Unavailable ${period}`);
        }
        if (dayShifts.some(s => String(s.employee_id) === String(e.id) && s.shift_name === period)) reasons.push('Already scheduled');
        const empShiftHrs = shifts.filter(s => String(s.employee_id) === String(e.id)).reduce((a, s) => a + calcHours(s.start_time, s.end_time), 0);
        const maxH = e.max_hours_per_week || 40;
        if (empShiftHrs + periodHrs > maxH) reasons.push(`Max hours (${Math.round(empShiftHrs)}/${maxH})`);
        if (period === 'Breakfast') {
          const prevDate = new Date(date); prevDate.setDate(prevDate.getDate() - 1);
          if (shifts.some(s => String(s.employee_id) === String(e.id) && s.day === formatDate(prevDate) && s.shift_name === 'Dinner')) reasons.push('Clopen');
        }
        if (period === 'Dinner') {
          const nextDate = new Date(date); nextDate.setDate(nextDate.getDate() + 1);
          if (shifts.some(s => String(s.employee_id) === String(e.id) && s.day === formatDate(nextDate) && s.shift_name === 'Breakfast')) reasons.push('Clopen');
        }
        const isScheduledHere = dayShifts.some(s => String(s.employee_id) === String(e.id) && s.shift_name === period && s.role === role);
        return { name: e.name, reasons, scheduled: isScheduledHere };
      })
      .filter(e => !e.scheduled)
      .sort((a, b) => a.reasons.length - b.reasons.length);
  }

  function handleNeedMoreClick(e, period, role, date, di) {
    e.stopPropagation();
    const key = `${di}-${period}-${role}`;
    if (needMoreTooltip?.key === key) { setNeedMoreTooltip(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const items = getRejectionReasons(period, role, date);
    setNeedMoreTooltip({ key, x: rect.left, y: rect.bottom + 4, items, period, role });
  }

  const totalShifts = shifts.length;
  const totalHours = shifts.reduce((acc, s) => acc + calcHours(s.start_time, s.end_time), 0);
  const gridRows = getGridRows();
  let lastPeriod = null;

  function getScoreColor(s) { return s >= 80 ? 'text-green-600' : s >= 60 ? 'text-amber-500' : 'text-red-500'; }
  function getScoreBg(s) { return s >= 80 ? 'bg-green-50 border-green-200' : s >= 60 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'; }
  function getScoreLabel(s) { return s >= 90 ? 'Excellent' : s >= 80 ? 'Good' : s >= 60 ? 'Fair' : 'Needs work'; }
  // Meal-period sections — GRID context (Part 1): solid 100-level functional color
  // background + colored text. See DESIGN_SYSTEM.md "Meal period sections" for the
  // context-dependent rule (card-based contexts use the tonal --meal-bg-* vars).
  const periodStyle = {
    'Breakfast': { background: 'var(--meal-band-bg-breakfast)',  color: 'var(--meal-band-text-breakfast)' },
    'Lunch':     { background: 'var(--meal-band-bg-lunch)',      color: 'var(--meal-band-text-lunch)' },
    'Dinner':    { background: 'var(--meal-band-bg-dinner)',     color: 'var(--meal-band-text-dinner)' },
    'Late Night':{ background: 'var(--meal-band-bg-latenight)',  color: 'var(--meal-band-text-latenight)' },
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-6 py-2.5 flex items-center justify-between bg-white" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2">
          <GhostIconButton onClick={() => setWeekOffset(w => w - 1)} aria-label="Previous week">‹</GhostIconButton>
          <div className="text-center flex items-center gap-2" style={{ minWidth: 200 }}>
            <span className="text-sm font-medium text-gray-900">{weekLabel}</span>
            {published
              ? <StatusPill tone="green">Published</StatusPill>
              : <StatusPill tone="amber">Draft</StatusPill>}
          </div>
          <GhostIconButton onClick={() => setWeekOffset(w => w + 1)} aria-label="Next week">›</GhostIconButton>
          {weekOffset !== 0 && (
            <button
              onClick={() => setWeekOffset(0)}
              className="text-xs font-medium px-2 py-1 rounded-md"
              style={{ color: 'var(--text-secondary)' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-primary)'; e.currentTarget.style.background = 'var(--accent-subtle)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
            >
              Today
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {score && (
            <button onClick={() => setShowScoreModal(true)} className={`score-badge ${getScoreBg(score.score)}`}>
              <span className={getScoreColor(score.score)}>Score: {score.score}</span>
            </button>
          )}
          <SegmentedPills
            value={filterType}
            onChange={setFilterType}
            options={[
              { key: 'All', title: 'Show every role' },
              { key: 'FOH', title: 'Front of House — servers, bartenders, hosts, bussers, managers' },
              { key: 'BOH', title: 'Back of House — cooks, prep cooks, dishwashers' },
            ]}
          />
          <button onClick={() => setShowHelpPopover(v => !v)}
            title="Schedule tips" aria-label="Schedule tips"
            className="no-print flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: showHelpPopover ? 'var(--bg-hover)' : 'transparent', color: 'var(--text-secondary)', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontStyle: 'italic', fontFamily: 'Georgia, serif', position: 'relative' }}
            onMouseEnter={e => { if (!showHelpPopover) e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { if (!showHelpPopover) e.currentTarget.style.background = 'transparent'; }}
          >
            ?
            {showHelpPopover && (
              <div onClick={e => e.stopPropagation()}
                style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 280, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', padding: 16, textAlign: 'left', fontFamily: 'inherit', fontStyle: 'normal', fontWeight: 400, color: 'var(--text-primary)', zIndex: 60 }}>
                <div style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 12 }}>Schedule tips</div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 14, lineHeight: 1.5, color: 'var(--text-primary)' }}>
                  <li className="mb-1.5"><b>Click a shift</b> to edit time or set an end code (BD / CL).</li>
                  <li className="mb-1.5"><b>Drag a shift</b> onto another employee to reassign.</li>
                  <li className="mb-1.5"><b>By Role view:</b> click an employee name for their weekly pay and hours.</li>
                  <li className="mb-1.5"><b>Day / Week / By Role</b> toggle is here — switch how the schedule is grouped.</li>
                  <li><b>FOH / BOH:</b> front- vs back-of-house filter.</li>
                </ul>
              </div>
            )}
          </button>
          <SegmentedPills
            value={viewMode}
            onChange={setViewMode}
            options={[
              { key: 'day', label: 'Day' },
              { key: 'week', label: 'Week' },
              { key: 'role', label: 'By Role' },
            ]}
          />
          <GhostIconButton onClick={() => window.print()} aria-label="Print schedule" title="Print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
          </GhostIconButton>
          <button
            onClick={generateSchedule}
            disabled={generating}
            className="text-sm font-medium flex items-center gap-1.5 no-print transition-colors disabled:opacity-50"
            style={{ background: 'var(--accent-primary)', color: '#FFFFFF', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer' }}
            onMouseEnter={e => { if (!generating) e.currentTarget.style.background = 'var(--accent-primary-hover)'; }}
            onMouseLeave={e => { if (!generating) e.currentTarget.style.background = 'var(--accent-primary)'; }}
          >
            {generating ? (
              <><span className="animate-spin">⟳</span> Generating…</>
            ) : (
              <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.8 4.5L18 9l-4.2 1.5L12 15l-1.8-4.5L6 9l4.2-1.5L12 3z"/><path d="M19 13l.9 2.2L22 16l-2.1.8L19 19l-.9-2.2L16 16l2.1-.8L19 13z"/></svg> AI Generate</>
            )}
          </button>
          {/* Published state is persisted per (company, week) in schedule_weeks. The badge
              reflects actual state; publishJustFired only drives the entrance animation + toast. */}
          {published ? (
            <div className="inline-flex items-center gap-2">
              <span
                className={`${publishJustFired ? 'publish-badge-in ' : ''}text-xs font-medium inline-flex items-center gap-1`}
                style={{ background: 'var(--bg-success-subtle)', color: 'var(--text-success)', padding: '6px 10px', borderRadius: 6 }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Published
              </span>
              <button
                onClick={unpublishSchedule}
                disabled={unpublishing}
                className="text-xs font-medium no-print transition-colors disabled:opacity-50"
                style={{ background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', padding: '6px 10px', borderRadius: 6, cursor: 'pointer' }}
                onMouseEnter={e => { if (!unpublishing) { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; } }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                {unpublishing ? 'Unpublishing…' : 'Unpublish'}
              </button>
            </div>
          ) : (
            <button
              onClick={publishSchedule}
              className="text-sm font-medium no-print transition-colors"
              style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#262626')}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--text-primary)')}
            >
              Publish
            </button>
          )}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          @page { size: landscape; margin: 0.25in; }
          body, html { background: white !important; }
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; color-adjust: exact; -webkit-print-color-adjust: exact; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; font-size: 10px !important; }
          .no-print, .need-more-indicator, .add-shift-btn, .shift-delete-btn { display: none !important; }
          .print-header { display: flex !important; margin-bottom: 8px !important; }
          .print-area .rounded-xl { border-radius: 4px !important; }
          .print-area [class*="px-"] { padding-left: 4px !important; padding-right: 4px !important; }
          .print-area [class*="py-"] { padding-top: 2px !important; padding-bottom: 2px !important; }
          .print-area [class*="gap-"] { gap: 2px !important; }
          .print-area [class*="mb-"] { margin-bottom: 4px !important; }
          .print-area [class*="p-6"] { padding: 4px !important; }
          .print-area .min-h-9 { min-height: 20px !important; }
          .dark .print-area, .dark .print-area * { background: white !important; color: #111 !important; border-color: #ddd !important; }
        }
        .print-header { display: none; }
      `}} />
      {/* Stats row — moved out of the scroll container so it stays visible while scrolling the grid. */}
      <div className="px-6 py-2 flex items-center gap-6 no-print bg-white" style={{ borderBottom: '1px solid var(--border)' }}>
        {[{ label: 'Shifts', value: totalShifts }, { label: 'Hours', value: Math.round(totalHours) }, { label: 'Staff', value: employees.length }].map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</span>
            <span className="text-gray-900" style={{ fontSize: 14, fontWeight: 600 }}>{s.value}</span>
          </div>
        ))}
        {timeOffData.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12l3-3h4.5l1.5-3h2l1.5 3H20l3 3"/><path d="M3 12v6a2 2 0 002 2h14a2 2 0 002-2v-6"/></svg>
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{timeOffData.length} off:</span>
            <span style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 500 }}>{timeOffData.map(t => t.name.split(' ')[0]).join(', ')}</span>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-6 pb-6 bg-gray-50 print-area">
        <div className="print-header items-center justify-between mb-4 px-2">
          <div className="text-lg font-semibold text-gray-900">Schedule</div>
          <div className="text-sm text-gray-500">{weekLabel}</div>
        </div>

        {viewMode === 'day' ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Day picker — simple tab strip, active day = black pill, today = purple accent. */}
            <div className="flex items-center gap-1 px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
              {DAYS.map((d, i) => {
                const isToday = formatDate(weekDates[i]) === formatDate(new Date());
                const isActive = ganttDayIndex === i;
                let style;
                if (isActive) style = { background: 'var(--text-primary)', color: 'var(--bg-card)' };
                else if (isToday) style = { background: 'transparent', color: 'var(--accent-primary)' };
                else style = { background: 'transparent', color: 'var(--text-secondary)' };
                return (
                  <button
                    key={d}
                    onClick={() => setGanttDayIndex(i)}
                    className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                    style={style}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {d} <span className="font-semibold">{weekDates[i].getDate()}</span>
                  </button>
                );
              })}
            </div>
            <DayTimeline
              companyId={companyId}
              date={formatDate(weekDates[ganttDayIndex])}
              hourStart={6}
              hourEnd={24}
              editable={true}
              filterType={filterType}
              onShiftsChanged={fetchData}
            />
          </div>
        ) : viewMode === 'role' ? (
          <>
          {dragError && (
            <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-md" style={{ background: 'var(--bg-critical-subtle)', borderLeft: '3px solid #DC2626', color: 'var(--text-critical)', fontSize: 12, fontWeight: 500 }}>
              <span>&#9888;</span>
              <span className="flex-1">{dragError}</span>
              <button onClick={() => setDragError(null)} style={{ fontSize: 14, color: 'var(--text-critical)' }}>&#x2715;</button>
            </div>
          )}
          <div className="schedule-grid" style={{ borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
            <div className="grid" style={{ gridTemplateColumns: '180px repeat(7, 1fr)', borderBottom: '1px solid var(--border)', background: 'var(--bg-card)', position: 'sticky', top: 0, zIndex: 40, borderTopLeftRadius: 8, borderTopRightRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.04)' }}>
              <div className="px-3 py-2 flex items-center" style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>Employee</div>
              {weekDates.map((d, i) => {
                const isToday = formatDate(d) === formatDate(new Date());
                return (
                  <div key={i} className="text-center" style={{ padding: '6px 4px', borderLeft: '1px solid var(--border)', fontSize: 12, color: isToday ? 'var(--accent-primary)' : 'var(--text-secondary)', background: isToday ? 'var(--accent-subtle)' : 'transparent' }}>
                    <span style={{ fontWeight: 500 }}>{DAYS[i]} </span>
                    <span className="text-gray-900" style={{ fontWeight: 600, color: isToday ? 'var(--accent-primary)' : undefined }}>{d.getDate()}</span>
                  </div>
                );
              })}
            </div>
            {/* ── EXTRAS BAND ─────────────────────────────────────────────────
                Lists one-off and recurring "extra" shifts (prep, cleaning, etc)
                that don't fit the normal period/role staffing. */}
            {(() => {
              const isExtrasCollapsed = !!collapsedRoles['role-view-__extras'];
              const extrasHours = extraShifts.reduce((a, s) => a + calcHours(s.start_time, s.end_time), 0);
              const extrasCost = extraShifts.reduce((a, s) => {
                const emp = employees.find(e => String(e.id) === String(s.employee_id));
                const rate = Number(emp?.hourly_rate) || 0;
                return a + calcHours(s.start_time, s.end_time) * rate;
              }, 0);
              return (
                <React.Fragment>
                  <div className="grid" style={{ gridTemplateColumns: '180px repeat(7, 1fr)' }}>
                    {/* Extras band — GRID context: solid teal 100 fill, same pattern as meal bands. */}
                    <div className="col-span-8 flex items-center gap-3" style={{
                      padding: '4px 12px',
                      background: 'var(--meal-band-bg-extras)',
                      borderTop: '1px solid var(--border)',
                      borderBottom: '1px solid var(--border)',
                      color: 'var(--meal-band-text-extras)',
                    }}>
                      <span onClick={() => setCollapsedRoles(c => ({ ...c, 'role-view-__extras': !c['role-view-__extras'] }))} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: isExtrasCollapsed ? 'rotate(-90deg)' : 'rotate(0)', fontSize: 10 }}>▾</span>
                        Extras
                        <span style={{ opacity: 0.7, fontWeight: 500 }}>· {extraShifts.length}</span>
                      </span>
                      <span style={{ display: 'flex', gap: 12, fontSize: 11, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                        <span><span style={{ opacity: 0.7 }}>Hrs </span>{extrasHours.toFixed(1)}</span>
                        {extrasCost > 0 && <span><span style={{ opacity: 0.7 }}>$ </span>{extrasCost.toFixed(0)}</span>}
                      </span>
                      <button
                        onClick={() => setExtraModal({ mode: 'create', specific_date: formatDate(weekDates[0]), day_of_week: DAYS[0], role: allRoles[0] || '', employee_id: '', start_time: '09:00', end_time: '13:00', label: '', is_recurring: false })}
                        className="transition-colors"
                        style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-primary)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '2px 10px', cursor: 'pointer', textTransform: 'none', letterSpacing: 0 }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                      >
                        + Add extra
                      </button>
                    </div>
                  </div>
                  {!isExtrasCollapsed && (extraShifts.length === 0 ? (
                    <div className="grid" style={{ gridTemplateColumns: '180px repeat(7, 1fr)', borderBottom: '1px solid #F3F4F6' }}>
                      <div className="col-span-8 px-3 py-2 text-xs text-center" style={{ color: 'var(--text-muted)', background: 'var(--bg-subtle)', fontStyle: 'italic' }}>No extra shifts this week. Add prep, cleaning, or one-off tasks with the button above.</div>
                    </div>
                  ) : (() => {
                    // Group extras by a row identity so one "Prep" row collects all its days.
                    // Recurring rows get one row per original id; one-offs get one row per id (each day appears once).
                    const rowKey = (x) => `ex-${x.id}`;
                    const rowsMap = new Map();
                    extraShifts.forEach(x => { rowsMap.set(rowKey(x), x); });
                    const rows = [...rowsMap.values()];
                    return rows.map(x => {
                      const emp = employees.find(e => String(e.id) === String(x.employee_id));
                      const label = x.label || 'Extra';
                      const hrs = calcHours(x.start_time, x.end_time);
                      return (
                        <div key={rowKey(x)} className="grid" style={{ gridTemplateColumns: '180px repeat(7, 1fr)', borderBottom: '1px solid var(--border)' }}>
                          <button onClick={() => setExtraModal({ mode: 'edit', id: x.id, specific_date: x.specific_date || formatDate(weekDates[0]), day_of_week: x.day_of_week || DAYS[0], role: x.role || '', employee_id: x.employee_id || '', start_time: x.start_time, end_time: x.end_time, label: x.label || '', is_recurring: !!x.is_recurring })}
                            className="px-3 py-2 flex flex-col justify-center text-left hover:bg-gray-50"
                            style={{ background: 'var(--bg-subtle)', borderRight: '1px solid var(--border)', border: 0, cursor: 'pointer' }}>
                            <div className="text-xs font-semibold truncate text-gray-900">
                              {label}
                              {x.is_recurring && <span style={{ fontSize: 9, fontWeight: 500, color: '#0D9488', background: '#CCFBF1', borderRadius: 6, padding: '1px 6px', marginLeft: 6 }}>R</span>}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                              {emp ? emp.name : 'Unassigned'}{x.role ? ` · ${x.role}` : ''}{hrs > 0 ? ` · ${hrs.toFixed(1)}h` : ''}
                            </div>
                          </button>
                          {weekDates.map((date, di) => {
                            const dateStr = formatDate(date);
                            const isToday = dateStr === formatDate(new Date());
                            const showHere = x.day === dateStr;
                            return (
                              <div key={di} className="px-1 py-1 min-h-9 flex flex-col gap-0.5" style={{ borderLeft: '1px solid var(--border)', background: isToday ? 'var(--accent-subtle)' : 'var(--bg-card)' }}>
                                {showHere && (
                                  <div className="px-1.5 py-0.5 rounded-lg text-xs truncate w-full"
                                    style={{ background: '#CCFBF1', color: '#115E59', borderLeft: '3px solid #0D9488', lineHeight: 1.3, fontWeight: 600 }}
                                    title={`${label}${emp ? ' · ' + emp.name : ''} · ${shortTime(x.start_time)}–${shortTime(x.end_time)}`}>
                                    {shortTime(x.start_time)}–{shortTime(x.end_time)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    });
                  })())}
                </React.Fragment>
              );
            })()}
            {allRoles
              .filter(role => {
                if (filterType === 'FOH') return ROLE_TYPE[role] !== 'BOH';
                if (filterType === 'BOH') return ROLE_TYPE[role] === 'BOH';
                return true;
              })
              .map(role => {
                // An employee appears only under their PRIMARY role. If primary_role isn't set,
                // fall back to the first role in their CSV string (matching the edit form's default).
                const roleEmployees = employees.filter(e => {
                  const roles = typeof e.role === 'string' ? e.role.split(',').map(r => r.trim()).filter(Boolean) : (e.role || []);
                  const primary = e.primary_role || roles[0];
                  return primary === role;
                });
                const isCollapsed = !!collapsedRoles[`role-view-${role}`];
                // Role group totals: sum all shifts of this role (independent of which employees cover them).
                const roleShifts = shifts.filter(s => s.role === role);
                const roleHours = roleShifts.reduce((a, s) => a + calcHours(s.start_time, s.end_time), 0);
                const roleCost = roleShifts.reduce((a, s) => {
                  const emp = employees.find(e => String(e.id) === String(s.employee_id));
                  const rate = Number(emp?.hourly_rate) || 0;
                  return a + calcHours(s.start_time, s.end_time) * rate;
                }, 0);
                return (
                  <React.Fragment key={role}>
                    <div className="grid cursor-pointer" style={{ gridTemplateColumns: '180px repeat(7, 1fr)' }}
                      onClick={() => setCollapsedRoles(c => ({ ...c, [`role-view-${role}`]: !c[`role-view-${role}`] }))}>
                      {/* Role bands keep their solid data-color fill — role identity IS the signal here.
                          Typography tuned to the spec section-label rule (11 / 600 / uppercase / tracking). */}
                      <div className="col-span-8 flex items-center gap-3" style={{
                        padding: '8px 12px',
                        background: ROLE_SOLID[role] || 'var(--text-muted)',
                        color: '#FFFFFF',
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                      }}>
                        <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0)', fontSize: 10 }}>▾</span>
                        {role}
                        <span style={{ opacity: 0.75, fontWeight: 500 }}>· {roleEmployees.length}</span>
                        <span style={{ marginLeft: 'auto', display: 'flex', gap: 12, fontWeight: 500, fontVariantNumeric: 'tabular-nums', textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
                          <span><span style={{ opacity: 0.7 }}>Hrs </span>{roleHours.toFixed(1)}</span>
                          {roleCost > 0 && <span><span style={{ opacity: 0.7 }}>$ </span>{roleCost.toFixed(0)}</span>}
                        </span>
                      </div>
                    </div>
                    {!isCollapsed && (roleEmployees.length === 0 ? (
                      <div className="grid" style={{ gridTemplateColumns: '180px repeat(7, 1fr)', borderBottom: '1px solid var(--border)' }}>
                        <div className="col-span-8 px-3 py-2 text-xs text-center" style={{ color: 'var(--text-muted)', background: 'var(--bg-subtle)', fontStyle: 'italic' }}>No employees assigned to this role</div>
                      </div>
                    ) : roleEmployees.map(emp => {
                      // Weekly totals are computed across ALL of this employee's shifts (every role),
                      // so pay/OT are consistent even if they appear in multiple role sections.
                      const empAllShifts = shifts.filter(s => String(s.employee_id) === String(emp.id));
                      const empWeekHours = empAllShifts.reduce((a, s) => a + calcHours(s.start_time, s.end_time), 0);
                      const rate = Number(emp.hourly_rate) || 0;
                      const regHours = Math.min(empWeekHours, overtimeThreshold);
                      const otHours = Math.max(0, empWeekHours - overtimeThreshold);
                      const empWeekPay = regHours * rate + otHours * rate * 1.5;
                      const hasOT = otHours > 0;
                      // Show ALL of the employee's shifts under their primary-role band — shifts for
                      // other roles still appear here, but the pill renders in that role's color so
                      // the cross-role coverage is visible at a glance.
                      const empRoleShifts = empAllShifts;
                      return (
                        <div key={`${role}-${emp.id}`} className="grid" style={{ gridTemplateColumns: '180px repeat(7, 1fr)', borderBottom: '1px solid var(--border)' }}>
                          <div className="px-3 py-2 flex flex-col justify-center" style={{ background: 'var(--bg-subtle)', borderRight: '1px solid var(--border)' }}>
                            <button onClick={() => setProfileEmp(emp)}
                              className="text-left text-xs font-semibold truncate hover:underline text-gray-900"
                              style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer' }}>
                              {emp.name}
                            </button>
                            <div style={{ fontSize: 11, marginTop: 2, color: hasOT ? 'var(--text-critical)' : 'var(--text-secondary)', fontWeight: hasOT ? 500 : 400 }}>
                              {empWeekHours.toFixed(1)} hrs{rate > 0 ? ` · $${empWeekPay.toFixed(0)}` : ''}
                              {hasOT && <span style={{ marginLeft: 4, fontWeight: 500 }}>OT</span>}
                            </div>
                          </div>
                          {weekDates.map((date, di) => {
                            const dateStr = formatDate(date);
                            const isToday = dateStr === formatDate(new Date());
                            const cellShifts = empRoleShifts.filter(s => s.day === dateStr);
                            const dayName = DAYS[di];
                            const firstActivePeriod = (template[dayName] || [])[0] || 'Lunch';
                            return (
                              <div key={di} className="group/cell px-1 py-1 min-h-9 flex flex-col gap-0.5"
                                style={{ borderLeft: '1px solid var(--border)', background: isToday ? 'var(--accent-subtle)' : 'var(--bg-card)' }}
                                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                                onDrop={(e) => { e.preventDefault(); const shiftId = e.dataTransfer.getData('shiftId'); if (shiftId) reassignShift(shiftId, emp, role); }}>
                                {cellShifts.map(shift => {
                                  const isEditing = editingShift === shift.id;
                                  return (
                                    <div key={shift.id} className="relative">
                                      <div
                                        draggable={!isEditing}
                                        onDragStart={(e) => { e.dataTransfer.setData('shiftId', String(shift.id)); e.dataTransfer.effectAllowed = 'move'; }}
                                        onClick={() => { if (!isEditing) { setEditingShift(shift.id); setEditStart(shift.start_time); setEditEnd(shift.end_time); setEditEndCode(shift.end_code || ''); } }}
                                        title={`${emp.name} · ${shift.role} · ${shortTime(shift.start_time)}–${shift.end_code || shortTime(shift.end_time)} · click to edit, drag to reassign`}
                                        className="shift-card px-1.5 py-0.5 rounded-lg text-xs truncate cursor-pointer w-full"
                                        style={{
                                          background: ROLE_CARD_STYLE[shift.role]?.background || 'var(--bg-subtle)',
                                          color: ROLE_CARD_STYLE[shift.role]?.color || 'var(--text-primary)',
                                          borderLeft: `3px solid ${ROLE_CARD_STYLE[shift.role]?.borderColor || '#999'}`,
                                          lineHeight: '1.3',
                                          fontWeight: 600,
                                        }}>
                                        {shift.role !== role && <span style={{ fontWeight: 700, marginRight: 4 }}>{shift.role}</span>}
                                        {shortTime(shift.start_time)}–{shift.end_code || shortTime(shift.end_time)}
                                      </div>
                                      {isEditing && (
                                        <InlineEditPopover
                                          editStart={editStart} editEnd={editEnd}
                                          editEndCode={editEndCode} endCodes={endCodes}
                                          onStart={setEditStart} onEnd={setEditEnd} onCode={setEditEndCode}
                                          onSave={saveInlineEdit}
                                          onDelete={() => deleteShift(shift.id)}
                                          onClose={() => { setEditingShift(null); setEditEndCode(''); }}
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                                <button
                                  onClick={() => {
                                    const pd = PERIOD_DEFAULTS[firstActivePeriod] || { start: '10:00', end: '18:00' };
                                    const empRoles = typeof emp.role === 'string' ? emp.role.split(',').map(r => r.trim()).filter(Boolean) : (emp.role || []);
                                    const defaultRole = emp.primary_role || empRoles[0] || role;
                                    setModalCell({ date, period: firstActivePeriod, role: defaultRole });
                                    setSelectedRole(defaultRole);
                                    setSelectedPeriod(firstActivePeriod);
                                    setSelectedEmployee(String(emp.id));
                                    setCustomStart(pd.start);
                                    setCustomEnd(pd.end);
                                    setShowModal(true);
                                  }}
                                  className={`${cellShifts.length > 0 ? 'opacity-0 group-hover/cell:opacity-100' : 'opacity-60 hover:opacity-100'} transition-all px-1 py-0 rounded-md border border-dashed text-center add-shift-btn`}
                                  style={{ fontSize: 11, borderColor: 'var(--border)', color: 'var(--text-muted)' }}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                                  title="Add a shift for this person on this day">+</button>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }))}
                  </React.Fragment>
                );
              })}
          </div>
          </>
        ) : (
        <div className="schedule-grid" style={{ borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
          <div className="grid" style={{ gridTemplateColumns: '110px repeat(7, 1fr)', borderBottom: '1px solid var(--border)', background: 'var(--bg-card)', position: 'sticky', top: 0, zIndex: 40, borderTopLeftRadius: 8, borderTopRightRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.04)' }}>
            <div className="px-2 py-2 flex items-center" style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>Role</div>
            {weekDates.map((d, i) => {
              const isToday = formatDate(d) === formatDate(new Date());
              const altBg = i % 2 === 1 ? 'var(--bg-subtle)' : 'transparent';
              return (
                <div key={i} className="text-center" style={{ padding: '6px 4px', borderLeft: '1px solid var(--border)', fontSize: 12, color: isToday ? 'var(--accent-primary)' : 'var(--text-secondary)', background: isToday ? 'var(--accent-subtle)' : altBg }}>
                  <span style={{ fontWeight: 500 }}>{DAYS[i]} </span>
                  <span className="text-gray-900" style={{ fontWeight: 600, color: isToday ? 'var(--accent-primary)' : undefined }}>{d.getDate()}</span>
                </div>
              );
            })}
          </div>

          {gridRows.map((row) => {
            const showPeriodHeader = row.period !== lastPeriod;
            lastPeriod = row.period;
            return (
              <React.Fragment key={`${row.period}-${row.role}`}>
                {showPeriodHeader && (
                  <div className="grid cursor-pointer" style={{ gridTemplateColumns: '110px repeat(7, 1fr)' }}
                    onClick={() => setCollapsedPeriods(p => ({ ...p, [row.period]: !p[row.period] }))}>
                    {/* Meal-period band — GRID context per Part 1: solid 100-level functional
                        color fill, no left bar, ~28px row height, 10px/700/uppercase label.
                        Top + bottom border integrate the band with the grid dividers. */}
                    <div className="col-span-8 flex items-center gap-2" style={{
                      padding: '4px 12px',
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      background: periodStyle[row.period]?.background || 'var(--bg-subtle)',
                      color: periodStyle[row.period]?.color || 'var(--text-secondary)',
                      borderTop: '1px solid var(--border)',
                      borderBottom: '1px solid var(--border)',
                    }}>
                      <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: collapsedPeriods[row.period] ? 'rotate(-90deg)' : 'rotate(0)', fontSize: 10 }}>▾</span>
                      {row.period}
                    </div>
                  </div>
                )}
                {!collapsedPeriods[row.period] && (
                <div className="grid last:border-0" style={{ gridTemplateColumns: '110px repeat(7, 1fr)', borderBottom: '1px solid var(--border)' }}>
                  <div className="px-2 py-1 flex items-center" style={{ background: 'var(--bg-subtle)', borderRight: '1px solid var(--border)' }}>
                    <div className="text-xs font-medium truncate text-gray-900">{row.role}</div>
                  </div>
                  {weekDates.map((date, di) => {
                    const dayName = DAYS[di];
                    const isActive = (template[dayName] || []).includes(row.period);
                    const isToday = formatDate(date) === formatDate(new Date());
                    const cellShifts = getShiftsForCell(row.period, row.role, date);
                    const required = getRequiredCount(dayName, row.period, row.role);
                    const isSatisfied = required === 0 || cellShifts.length >= required;
                    return (
                      <div key={di} className="px-1 py-1 min-h-9 flex flex-col gap-0.5" style={{ borderLeft: '1px solid var(--border)', background: isToday ? 'var(--accent-subtle)' : !isActive ? 'var(--bg-subtle)' : (di % 2 === 1 ? 'var(--bg-subtle)' : 'var(--bg-card)') }}>
                        {!isActive ? (
                          <div className="text-xs text-center mt-2" style={{ color: 'var(--text-muted)' }}>—</div>
                        ) : (
                          <>
                            {cellShifts.map(shift => {
                              const emp = getEmployee(shift.employee_id);
                              const isRecurring = shift._isRecurring;
                              const isEditing = !isRecurring && editingShift === shift.id;
                              const isDouble = !isRecurring && cellShifts.filter(s => !s._isRecurring && String(s.employee_id) === String(shift.employee_id)).length > 1;
                              const dayDoubleCount = shifts.filter(s => s.day === shift.day && String(s.employee_id) === String(shift.employee_id)).length;
                              const showDouble = dayDoubleCount > 1;
                              return (
                                <div key={shift._previewId || shift.id}
                                  title={emp ? `${emp.name} · ${shortTime(shift.start_time)}–${shift.end_code || shortTime(shift.end_time)}` : `${shortTime(shift.start_time)}–${shift.end_code || shortTime(shift.end_time)}`}
                                  className="shift-card group relative flex items-center justify-between px-1.5 py-0.5 rounded-lg text-xs w-full"
                                  style={{
                                    background: ROLE_CARD_STYLE[shift.role]?.background || 'var(--bg-subtle)',
                                    color: ROLE_CARD_STYLE[shift.role]?.color || 'var(--text-primary)',
                                    borderLeft: `3px solid ${ROLE_CARD_STYLE[shift.role]?.borderColor || '#999'}`,
                                    lineHeight: '1.3',
                                    ...(isRecurring ? { borderStyle: 'dashed', opacity: 0.65, border: `1px dashed ${ROLE_CARD_STYLE[shift.role]?.borderColor || '#999'}`, borderLeft: `3px dashed ${ROLE_CARD_STYLE[shift.role]?.borderColor || '#999'}` } : {}),
                                    ...(shift.is_opener ? { borderTopWidth: 2, borderTopColor: 'var(--text-warning)', borderTopStyle: 'solid' } : {}),
                                    ...(shift.is_closer ? { borderBottomWidth: 2, borderBottomColor: 'var(--accent-primary)', borderBottomStyle: 'solid' } : {}),
                                  }}>
                                  {/* Two-line pill: name (with O/C badges) on top, time/code on bottom. */}
                                  <div className="flex-1 min-w-0 flex flex-col" style={{ lineHeight: 1.1 }}>
                                    <div className="flex items-center gap-1 truncate">
                                      <span className="font-semibold truncate">{emp ? emp.name.split(' ')[0] + ' ' + (emp.name.split(' ')[1]?.[0] || '') + '.' : '?'}</span>
                                      {shift.is_opener && <span style={{ fontSize: 9, fontWeight: 500, color: 'var(--text-warning)', background: 'var(--bg-warning-subtle)', borderRadius: 6, padding: '0 4px', flexShrink: 0 }}>O</span>}
                                      {shift.is_closer && <span style={{ fontSize: 9, fontWeight: 500, color: 'var(--accent-primary)', background: 'var(--bg-accent-chip)', borderRadius: 6, padding: '0 4px', flexShrink: 0 }}>C</span>}
                                    </div>
                                    <div style={{ fontSize: 11, opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                                      {shortTime(shift.start_time)}–{shift.end_code || shortTime(shift.end_time)}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    {showDouble && !isRecurring && <span title="This employee has another shift today" style={{ fontSize: 9, fontWeight: 500, color: 'var(--text-secondary)', background: 'var(--bg-subtle)', borderRadius: 6, padding: '0 4px' }}>2x</span>}
                                    {isRecurring ? (
                                      <div style={{ fontSize: 9, fontWeight: 500, color: 'var(--text-secondary)', background: 'var(--bg-subtle)', borderRadius: 6, padding: '0 4px' }}>R</div>
                                    ) : (
                                      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity shift-delete-btn">
                                        <button onClick={(e) => { e.stopPropagation(); setEditingShift(shift.id); setEditStart(shift.start_time); setEditEnd(shift.end_time); setEditEndCode(shift.end_code || ''); }} style={{ fontSize: 11, color: 'var(--text-secondary)' }} onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-primary)')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}>&#x270E;</button>
                                        <button onClick={() => deleteShift(shift.id)} style={{ fontSize: 11, color: 'var(--text-critical)' }}>&#x2715;</button>
                                      </div>
                                    )}
                                  </div>
                                  {isEditing && (
                                    <InlineEditPopover
                                      editStart={editStart} editEnd={editEnd}
                                      editEndCode={editEndCode} endCodes={endCodes}
                                      onStart={setEditStart} onEnd={setEditEnd} onCode={setEditEndCode}
                                      onSave={saveInlineEdit}
                                      onClose={() => { setEditingShift(null); setEditEndCode(''); }}
                                    />
                                  )}
                                </div>
                              );
                            })}
                            {required > 0 && !isSatisfied && (
                              <div onClick={(e) => handleNeedMoreClick(e, row.period, row.role, date, di)}
                                className="text-center need-more-indicator cursor-pointer transition-all"
                                style={{ position: 'relative', fontSize: 11, fontWeight: 500, background: 'var(--bg-critical-subtle)', borderLeft: '3px solid #DC2626', color: 'var(--text-critical)', borderRadius: 6, padding: '2px 6px' }}
                                onMouseEnter={e => (e.currentTarget.style.filter = 'brightness(0.97)')}
                                onMouseLeave={e => (e.currentTarget.style.filter = 'none')}
                              >
                                +{required - cellShifts.length}
                              </div>
                            )}
                            <button
                              onClick={() => openModal(date, row.period, row.role)}
                              className="transition-all text-center add-shift-btn"
                              style={{ fontSize: 11, border: '1px dashed #E5E7EB', color: 'var(--text-muted)', borderRadius: 6, padding: '0 6px', background: 'transparent' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                            >+</button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </React.Fragment>
            );
          })}
          <div className="flex gap-4 px-4 py-2.5 flex-wrap items-center" style={{ background: 'var(--bg-subtle)', borderTop: '1px solid var(--border)' }}>
            <div className="flex items-center gap-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}><div style={{ width: 10, height: 10, borderRadius: 3, background: '#BFDBFE', border: '1px solid #2563EB' }}></div>FOH</div>
            <div className="flex items-center gap-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}><div style={{ width: 10, height: 10, borderRadius: 3, background: '#FED7AA', border: '1px solid #EA580C' }}></div>BOH</div>
            <div className="flex items-center gap-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}><span style={{ fontSize: 9, fontWeight: 500, color: 'var(--text-secondary)', background: 'var(--bg-subtle)', borderRadius: 6, padding: '0 4px' }}>R</span>Recurring</div>
            <div className="flex items-center gap-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}><span style={{ fontSize: 9, fontWeight: 500, color: 'var(--text-warning)', background: 'var(--bg-warning-subtle)', borderRadius: 6, padding: '0 4px' }}>O</span>Opener</div>
            <div className="flex items-center gap-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}><span style={{ fontSize: 9, fontWeight: 500, color: 'var(--accent-primary)', background: 'var(--bg-accent-chip)', borderRadius: 6, padding: '0 4px' }}>C</span>Closer</div>
            <span className="ml-auto" style={{ fontSize: 12, color: 'var(--text-muted)' }}>Hover to edit</span>
          </div>
        </div>
        )}
      </div>

      {/* EXTRA SHIFT MODAL (create / edit) */}
      {extraModal && (() => {
        const m = extraModal;
        const saveExtra = async () => {
          const payload = {
            company_id: companyId,
            employee_id: m.employee_id ? parseInt(m.employee_id) : null,
            role: m.role || null,
            specific_date: m.is_recurring ? null : m.specific_date,
            day_of_week: m.is_recurring ? m.day_of_week : null,
            start_time: m.start_time,
            end_time: m.end_time,
            label: m.label || null,
            is_recurring: !!m.is_recurring,
            is_active: true,
          };
          if (m.mode === 'edit' && m.id) {
            await supabase.from('extra_shifts').update(payload).eq('id', m.id);
          } else {
            await supabase.from('extra_shifts').insert([payload]);
          }
          setExtraModal(null);
          fetchData();
        };
        const deleteExtra = async () => {
          if (!m.id) return;
          await supabase.from('extra_shifts').delete().eq('id', m.id);
          setExtraModal(null);
          fetchData();
        };
        return (
          <div className="modal-backdrop" onClick={() => setExtraModal(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg border border-gray-200 p-5 w-full max-w-sm mx-4">
              <div className="text-base font-semibold text-gray-900 mb-4">{m.mode === 'edit' ? 'Edit extra shift' : 'Add extra shift'}</div>
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                  <input type="checkbox" checked={m.is_recurring} onChange={e => setExtraModal({ ...m, is_recurring: e.target.checked })} />
                  Repeats every week
                </label>
                {m.is_recurring ? (
                  <div>
                    <div className="text-xs font-medium text-gray-600 mb-1">Day of week</div>
                    <select value={m.day_of_week} onChange={e => setExtraModal({ ...m, day_of_week: e.target.value })}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#6D28D9]">
                      {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                ) : (
                  <div>
                    <div className="text-xs font-medium text-gray-600 mb-1">Date</div>
                    <input type="date" value={m.specific_date} onChange={e => setExtraModal({ ...m, specific_date: e.target.value })}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#6D28D9]" />
                  </div>
                )}
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-1">Label</div>
                  <input type="text" value={m.label} onChange={e => setExtraModal({ ...m, label: e.target.value })}
                    placeholder="e.g. Prep, Deep clean"
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#6D28D9]" />
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-1">Role</div>
                  <select value={m.role} onChange={e => setExtraModal({ ...m, role: e.target.value })}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#6D28D9]">
                    <option value="">(none)</option>
                    {allRoles.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-1">Employee</div>
                  <select value={m.employee_id} onChange={e => setExtraModal({ ...m, employee_id: e.target.value })}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#6D28D9]">
                    <option value="">Unassigned</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="text-xs font-medium text-gray-600 mb-1">Start</div>
                    <input type="time" value={m.start_time} onChange={e => setExtraModal({ ...m, start_time: e.target.value })}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#6D28D9]" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-gray-600 mb-1">End</div>
                    <input type="time" value={m.end_time} onChange={e => setExtraModal({ ...m, end_time: e.target.value })}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#6D28D9]" />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-5">
                {m.mode === 'edit'
                  ? <button onClick={deleteExtra} className="text-red-500 hover:text-red-700 text-xs font-medium">Delete</button>
                  : <span />}
                <div className="flex items-center gap-2">
                  <button onClick={() => setExtraModal(null)} className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100">Cancel</button>
                  <button onClick={saveExtra} disabled={!m.start_time || !m.end_time || (m.is_recurring ? !m.day_of_week : !m.specific_date)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50" style={{ background: '#0D9488' }}>
                    {m.mode === 'edit' ? 'Save' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* EMPLOYEE PROFILE MODAL (By Role view) */}
      {profileEmp && (() => {
        const emp = profileEmp;
        const empShifts = shifts.filter(s => String(s.employee_id) === String(emp.id));
        const totalHrs = empShifts.reduce((a, s) => a + calcHours(s.start_time, s.end_time), 0);
        const rate = Number(emp.hourly_rate) || 0;
        const regHours = Math.min(totalHrs, overtimeThreshold);
        const otHours = Math.max(0, totalHrs - overtimeThreshold);
        const totalPay = regHours * rate + otHours * rate * 1.5;
        const roles = typeof emp.role === 'string' ? emp.role.split(',').map(r => r.trim()).filter(Boolean) : (emp.role || []);
        return (
          <div className="modal-backdrop" onClick={() => setProfileEmp(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg border border-gray-200 w-full max-w-md mx-4" style={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
              <div className="flex items-start justify-between p-5 border-b border-gray-100">
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-gray-900 truncate">{emp.name}</div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {roles.map(r => (
                      <span key={r} className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: ROLE_CARD_STYLE[r]?.background || 'var(--bg-subtle)', color: ROLE_CARD_STYLE[r]?.color || 'var(--text-primary)' }}>{r}</span>
                    ))}
                  </div>
                </div>
                <button onClick={() => setProfileEmp(null)} className="text-gray-400 hover:text-gray-600 text-lg px-2">×</button>
              </div>
              <div className="p-5 overflow-y-auto" style={{ flex: 1 }}>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="rounded-lg p-3" style={{ background: 'var(--bg-subtle)' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Hours</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: otHours > 0 ? 'var(--text-critical)' : 'var(--text-primary)', marginTop: 2 }}>{totalHrs.toFixed(1)}</div>
                    {otHours > 0 && <div style={{ fontSize: 10, color: 'var(--text-critical)', marginTop: 1 }}>{otHours.toFixed(1)}h OT</div>}
                  </div>
                  <div className="rounded-lg p-3" style={{ background: 'var(--bg-subtle)' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Pay</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>{rate > 0 ? `$${totalPay.toFixed(0)}` : '—'}</div>
                    {rate > 0 && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>@ ${rate.toFixed(2)}/hr</div>}
                  </div>
                  <div className="rounded-lg p-3" style={{ background: 'var(--bg-subtle)' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Shifts</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>{empShifts.length}</div>
                  </div>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6 }}>This week</div>
                <div className="space-y-1">
                  {weekDates.map((d, i) => {
                    const dateStr = formatDate(d);
                    const dayShifts = empShifts.filter(s => s.day === dateStr);
                    if (dayShifts.length === 0) return null;
                    return (
                      <div key={i} className="flex items-center gap-2 py-1" style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <div style={{ width: 60, fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{DAYS[i]} {d.getDate()}</div>
                        <div className="flex-1 flex flex-wrap gap-1">
                          {dayShifts.map(s => (
                            <span key={s.id} className="text-xs px-1.5 py-0.5 rounded" style={{ background: ROLE_CARD_STYLE[s.role]?.background || 'var(--bg-subtle)', color: ROLE_CARD_STYLE[s.role]?.color || 'var(--text-primary)', borderLeft: `3px solid ${ROLE_CARD_STYLE[s.role]?.borderColor || '#999'}` }}>
                              {s.role} · {shortTime(s.start_time)}–{s.end_code || shortTime(s.end_time)}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {empShifts.length === 0 && <div className="text-xs text-gray-400 italic">No shifts scheduled this week.</div>}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100">
                <button onClick={() => setProfileEmp(null)} className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-900 hover:bg-gray-100">Close</button>
                <button onClick={() => { setProfileEmp(null); if (typeof onNavigate === 'function') onNavigate('employees', { employeeId: emp.id }); }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors"
                  style={{ background: 'var(--accent-primary)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--accent-primary-hover)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'var(--accent-primary)')}
                >
                  Edit employee →
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ADD SHIFT MODAL */}
      {showModal && modalCell && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div className="modal-content bg-white rounded-lg border border-gray-200 p-6 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-base font-medium text-gray-900">Add shift</div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {modalCell.date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Role</label>
                <select value={selectedRole} onChange={e => setSelectedRole(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#6D28D9] bg-gray-50">
                  {allRoles.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Period</label>
                <select value={selectedPeriod} onChange={e => {
                  const p = e.target.value;
                  setSelectedPeriod(p);
                  const pd = PERIOD_DEFAULTS[p];
                  if (pd) { setCustomStart(pd.start); setCustomEnd(pd.end); }
                }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#6D28D9] bg-gray-50">
                  {MEAL_PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1.5">Employee</label>
              <select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#6D28D9] bg-gray-50">
                <option value="">Select employee...</option>
                {eligibleEmployees(selectedRole).map(emp => <option key={emp.id} value={emp.id} className={emp.disabled ? 'text-gray-400' : ''}>{emp.hasTimeOff ? '\u26A0\uFE0F ' : ''}{emp.name}{emp.reasons.length > 0 ? ` (${emp.reasons.join(', ')})` : ''}</option>)}
              </select>
              {selectedEmployee && hasTimeOff(parseInt(selectedEmployee), formatDate(modalCell.date)) && (
                <div className="mt-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700 flex items-center gap-1.5">
                  <span>\u26A0\uFE0F</span>
                  <span><strong>{employees.find(e => String(e.id) === String(selectedEmployee))?.name}</strong> has approved time off on this day</span>
                </div>
              )}
            </div>
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Start time</label>
                <input type="time" value={customStart} onChange={e => setCustomStart(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#6D28D9] bg-gray-50" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1.5">End time</label>
                <input type="time" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#6D28D9] bg-gray-50" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-4 cursor-pointer">
              <input type="checkbox" checked={newShiftRecurring} onChange={e => setNewShiftRecurring(e.target.checked)} />
              Repeats every week
            </label>
            <button onClick={saveShift} disabled={!selectedEmployee || saving} className="w-full text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-40 transition-colors"
              style={{ background: 'var(--text-primary)' }}
              onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = '#262626'; }}
              onMouseLeave={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--text-primary)'; }}
            >
              {saving ? 'Saving...' : (newShiftRecurring ? 'Add recurring shift' : 'Add shift')}
            </button>
          </div>
        </div>
      )}

      {/* CONFLICT MODAL */}
      {showConflicts && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div className="modal-content bg-white rounded-lg border border-gray-200 p-6 w-full max-w-md mx-4 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="text-base font-medium text-gray-900">Schedule conflicts</div>
              <button onClick={() => setShowConflicts(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            {conflicts.critical.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">🔴 Critical</div>
                <div className="flex flex-col gap-2">{conflicts.critical.map((c, i) => <div key={i} className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{c.message}</div>)}</div>
              </div>
            )}
            {conflicts.warnings.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">🟡 Warnings</div>
                <div className="flex flex-col gap-2">{conflicts.warnings.map((c, i) => <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">{c.message}</div>)}</div>
              </div>
            )}
            {conflicts.info.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-2">ℹ️ Info</div>
                <div className="flex flex-col gap-2">{conflicts.info.map((c, i) => <div key={i} className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-sm text-blue-600">{c.message}</div>)}</div>
              </div>
            )}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowConflicts(false)} className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2.5 text-sm hover:bg-gray-50">Fix issues</button>
              <button onClick={forcePublish}
                className="flex-1 text-white rounded-lg py-2.5 text-sm font-medium transition-colors"
                style={{ background: conflicts.critical.length > 0 ? 'var(--text-critical)' : 'var(--text-primary)' }}
                onMouseEnter={e => (e.currentTarget.style.filter = 'brightness(0.9)')}
                onMouseLeave={e => (e.currentTarget.style.filter = 'none')}
              >
                {conflicts.critical.length > 0 ? 'Override & publish' : 'Publish anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SCORE MODAL */}
      {showScoreModal && score && (() => {
        const totalSlots = score.shiftsTotal || score.shiftsFilled;
        const filledPct = totalSlots > 0 ? Math.round((score.shiftsFilled / totalSlots) * 100) : 100;
        const pctColor = filledPct >= 100 ? 'bg-green-500' : filledPct >= 90 ? 'bg-amber-500' : 'bg-red-500';
        const unfilled = score.unfilledSlots || [];
        const groupedUnfilled = {};
        unfilled.forEach(u => { if (!groupedUnfilled[u.date]) groupedUnfilled[u.date] = { day: u.day, date: u.date, items: [] }; groupedUnfilled[u.date].items.push(u); });

        return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div className="modal-content bg-white rounded-lg border border-gray-200 p-6 w-full max-w-lg mx-4" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
            <div className="flex items-center justify-between mb-5">
              <div className="text-base font-medium text-gray-900">Schedule report</div>
              <button onClick={() => setShowScoreModal(false)} className="text-gray-400 hover:text-gray-600 text-lg">&#x2715;</button>
            </div>

            {/* Shifts filled headline */}
            <div className="mb-5">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-2xl font-bold text-gray-900">{score.shiftsFilled} <span className="text-sm font-normal text-gray-400">of {totalSlots} shifts filled</span></div>
                <div className={`text-sm font-semibold ${filledPct >= 100 ? 'text-green-600' : filledPct >= 90 ? 'text-amber-600' : 'text-red-600'}`}>{filledPct}%</div>
              </div>
              <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className={`h-full rounded-full ${pctColor}`} style={{ width: `${Math.min(filledPct, 100)}%`, transition: 'width 0.5s ease' }} />
              </div>
            </div>

            {/* Score */}
            <div className={`rounded-xl border p-4 text-center mb-4 ${getScoreBg(score.score)}`}>
              <div className={`text-4xl font-bold mb-0.5 ${getScoreColor(score.score)}`}>{score.score}</div>
              <div className={`text-xs font-medium ${getScoreColor(score.score)}`}>{getScoreLabel(score.score)} schedule</div>
            </div>

            {/* All filled success */}
            {unfilled.length === 0 && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center text-sm text-green-700 mb-4">&#x2713; All shifts filled — you're good to go!</div>
            )}

            {/* Deductions */}
            {score.deductions.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Deductions</div>
                {score.deductions.map((d, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-1">
                    <div className="text-xs text-gray-700">{d.text}</div>
                    <div className="text-xs font-medium text-red-500 flex-shrink-0">{d.pts}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Unfilled shifts detail */}
            {unfilled.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-medium text-red-500 uppercase tracking-wide mb-2">{unfilled.reduce((s, u) => s + u.remaining, 0)} shifts need attention</div>
                {Object.values(groupedUnfilled).map(group => (
                  <div key={group.date} className="mb-3">
                    <div className="text-xs font-semibold text-gray-700 mb-1.5">{new Date(group.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</div>
                    {group.items.map((u, i) => (
                      <div key={i} className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-1 flex items-start justify-between">
                        <div>
                          <div className="text-xs font-medium text-gray-800">{u.period} &middot; {u.role} &middot; <span className="text-red-500">need {u.remaining} more</span></div>
                          {u.reasons.length > 0 && <div className="text-xs text-gray-400 mt-0.5">{u.reasons.slice(0, 3).join(' · ')}</div>}
                        </div>
                        <button onClick={() => {
                          setShowScoreModal(false);
                          const d = weekDates.find(wd => formatDate(wd) === u.date);
                          if (d) openModal(d, u.period, u.role);
                        }} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex-shrink-0 ml-2">Fill &#x2192;</button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setShowScoreModal(false)} className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2.5 text-sm hover:bg-gray-50">Close</button>
              <button onClick={() => { setShowScoreModal(false); publishSchedule(); }}
                className="flex-1 text-white rounded-lg py-2.5 text-sm font-medium transition-colors"
                style={{ background: 'var(--text-primary)' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#262626')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--text-primary)')}
              >Publish schedule</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* NEED MORE TOOLTIP */}
      {needMoreTooltip && (
        <>
          <div onClick={() => setNeedMoreTooltip(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 45 }} />
          <div style={{
            position: 'fixed', left: Math.min(needMoreTooltip.x, window.innerWidth - 300), top: needMoreTooltip.y,
            zIndex: 50, background: 'var(--bg-elevated)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: '1px solid var(--border)', padding: 0, minWidth: 260, maxWidth: 320, maxHeight: 300, overflow: 'hidden',
          }}>
            <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>Why not scheduled?</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{needMoreTooltip.role} · {needMoreTooltip.period}</div>
              </div>
              <button onClick={() => setNeedMoreTooltip(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, padding: 4 }}>✕</button>
            </div>
            <div style={{ maxHeight: 230, overflowY: 'auto', padding: '4px 6px' }}>
              {needMoreTooltip.items.length === 0 ? (
                <div style={{ padding: '16px 10px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>No employees with this role</div>
              ) : needMoreTooltip.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 8px', borderRadius: 8, background: i % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: item.reasons.length === 0 ? 'var(--text-success)' : 'var(--text-secondary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                  <span style={{ fontSize: 10, fontWeight: 500, color: item.reasons.length === 0 ? 'var(--text-success)' : 'var(--text-critical)', flexShrink: 0, marginLeft: 8, textAlign: 'right', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.reasons.length === 0 ? 'Available' : item.reasons.join(' · ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* GANTT TOOLTIP */}
      {ganttTooltip && (
        <div style={{ position: 'fixed', left: ganttTooltip.x, top: ganttTooltip.y, transform: 'translate(-50%, -100%)', zIndex: 100, pointerEvents: 'none' }}>
          <div style={{ background: '#1f2937', color: '#fff', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', lineHeight: '1.5', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', whiteSpace: 'nowrap' }}>
            <div style={{ fontWeight: 700 }}>{ganttTooltip.name}</div>
            <div style={{ color: '#9ca3af' }}>{ganttTooltip.role}</div>
            <div style={{ marginTop: '2px' }}>{ganttTooltip.time} ({ganttTooltip.hours})</div>
            {ganttTooltip.cost && <div style={{ color: '#4ade80', fontWeight: 600 }}>{ganttTooltip.cost}</div>}
          </div>
        </div>
      )}

      {/* Publish toast — fires on successful publish, lives 2.5s per CSS animation. */}
      {publishJustFired && (
        <div
          className="publish-toast fixed no-print"
          style={{
            right: 24, bottom: 24, zIndex: 100,
            background: 'var(--text-primary)', color: 'var(--bg-card)',
            padding: '10px 16px', borderRadius: 8,
            fontSize: 13, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', gap: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          Schedule published
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Design-system primitives scoped to this file ───────────────────── */

// Ghost icon button per DESIGN_SYSTEM.md Buttons → Ghost.
function GhostIconButton({ onClick, children, title, 'aria-label': ariaLabel }) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      className="flex items-center justify-center rounded-md transition-colors"
      style={{
        width: 28, height: 28,
        background: 'transparent', border: 'none',
        color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
    >
      {children}
    </button>
  );
}

// Canonical segmented pill toggle — view-switcher (same data, different shape).
// Per DESIGN_SYSTEM.md Tabs → Segmented pills. Container #F5F5F5, border #E5E7EB, 6px,
// 2px inner pad; active white + #0A0A0A + subtle shadow; inactive #525252, hover #0A0A0A.
function SegmentedPills({ value, onChange, options }) {
  return (
    <div
      className="no-print inline-flex"
      style={{ background: 'var(--bg-hover)', border: '1px solid var(--border)', borderRadius: 6, padding: 2 }}
    >
      {options.map(opt => {
        const key = opt.key;
        const label = opt.label || opt.key;
        const active = value === key;
        return (
          <button
            key={key}
            title={opt.title}
            onClick={() => onChange(key)}
            className="transition-colors font-medium"
            style={{
              background: active ? 'var(--bg-card)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderRadius: 6,
              padding: '6px 12px',
              fontSize: 14,
              border: 'none', cursor: 'pointer',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            }}
            onMouseEnter={e => { if (!active) e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// Inline edit popover used on shift cards (Week + By Role views). Elevated shadow per spec
// (0 4px 12px rgba(0,0,0,0.08)), 8px radius, purple focus ring on inputs via the form-input rule.
function InlineEditPopover({ editStart, editEnd, editEndCode, endCodes, onStart, onEnd, onCode, onSave, onDelete, onClose }) {
  return (
    <div
      onClick={e => e.stopPropagation()}
      className="absolute top-full left-0 mt-1 z-30 p-2 flex flex-col gap-1"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 240 }}
    >
      <div className="flex items-center gap-1">
        <input type="time" value={editStart} onChange={e => onStart(e.target.value)} className="rounded-md px-2 py-1 text-xs w-20 focus:outline-none" style={{ border: '1px solid var(--border)' }} />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>–</span>
        <input type="time" value={editEnd} onChange={e => onEnd(e.target.value)} disabled={!!editEndCode} className="rounded-md px-2 py-1 text-xs w-20 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400" style={{ border: '1px solid var(--border)' }} />
        <select value={editEndCode} onChange={e => onCode(e.target.value)} className="rounded-md px-2 py-1 text-xs focus:outline-none" style={{ border: '1px solid var(--border)', maxWidth: 80 }}>
          <option value="">Time</option>
          {endCodes.map(ec => <option key={ec.code} value={ec.code}>{ec.code}</option>)}
        </select>
      </div>
      <div className="flex items-center justify-end gap-1">
        <button onClick={onSave} className="text-white rounded-md px-2.5 py-1 text-xs font-medium transition-colors" style={{ background: 'var(--text-primary)' }} onMouseEnter={e => (e.currentTarget.style.background = '#262626')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--text-primary)')}>Save</button>
        {onDelete && <button onClick={onDelete} className="text-xs px-1" style={{ color: 'var(--text-critical)' }}>&#x1F5D1;</button>}
        <button onClick={onClose} className="text-xs px-1" style={{ color: 'var(--text-secondary)' }}>&#x2715;</button>
      </div>
    </div>
  );
}

// Status pill (state signaling) — uses the badge spec: 6px radius, 11px / 500, light
// functional bg + dark functional text.
function StatusPill({ tone = 'neutral', children }) {
  const palette = {
    green: { background: 'var(--bg-success-subtle)', color: 'var(--text-success)' },
    amber: { background: 'var(--bg-warning-subtle)', color: 'var(--text-warning)' },
    red:   { background: 'var(--bg-critical-subtle)', color: 'var(--text-critical)' },
    blue:  { background: 'var(--bg-info-subtle)', color: 'var(--text-info)' },
    neutral: { background: 'var(--bg-subtle)', color: 'var(--text-secondary)' },
  }[tone] || { background: 'var(--bg-subtle)', color: 'var(--text-secondary)' };
  return (
    <span
      className="inline-flex items-center text-[11px] font-medium"
      style={{ background: palette.background, color: palette.color, padding: '2px 8px', borderRadius: 6 }}
    >
      {children}
    </span>
  );
}